/**
 * Handles selection logic for a multi-select with a special "ALL" option.
 *
 * @param prevSelected - Previously selected options as a string array.
 * @param changedOption - The option that was just changed (checked or unchecked).
 * @param options - All available options, where the first item is treated as the "ALL" option.
 * @param checked - Boolean indicating whether the changed option was checked (true) or unchecked (false).
 * @returns An array of strings representing the new selection state.
 */
export function handleMultiSelectWithAll(
  prevSelected: string[],
  changedOption: string,
  options: readonly string[],
  checked: boolean
): string[] {
  const ALL = options[0];

  // If the changed option is "ALL", select or clear all options accordingly
  if (changedOption === ALL) {
    return checked ? [...options] : [];
  }

  // If all options were previously selected and an option (not ALL) was unchecked,
  // remove that option and the ALL option from the selection to reflect partial selection
  if (prevSelected.length === options.length && !checked) {
    return prevSelected.filter((opt) => opt !== ALL && opt !== changedOption);
  }

  // If an option was checked, add it to the selection.
  // If this results in all options except the "ALL" option being selected,
  // treat it as if all options are selected by adding "ALL".
  if (checked) {
    const newSelected = [...prevSelected, changedOption];
    if (newSelected.length === options.length - 1) {
      return [...options];
    }
    return newSelected;
  }

  // If an option was unchecked, remove it and the ALL option from the selection
  return prevSelected.filter((opt) => opt !== changedOption && opt !== ALL);
}
